import { yupResolver } from '@hookform/resolvers/yup'
import { Autocomplete, Grid, TextField, Typography } from '@mui/material'
import { useUser } from '@src/contexts/AppContext'
import { useMerchantById, useMerchantByParentId } from '@src/data/api/merchants-api/merchants-api'
import {
  CreditRatingOptions,
  PersonalLoanCalculatorConstants,
  PersonalLoanCalculatorGridSizes,
  PersonalLoanCalculatorPaymentOption,
  PersonalLoanCalculatorTermOption,
} from '@src/types/Constants'
import { Merchant } from '@src/types/Merchant'
import { PersonalLoanCalculatorSchema, PersonalLoanCalculatorSchemaType } from '@src/types/PersonalLoanCalculatorSchema'
import { useState, memo, useMemo } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  useCalculatorData,
  usePaymentOptions,
  useTermOptions,
  useAutoSelection,
} from './PersonalLoanCalculator/personalLoanCalculator-hooks'
import { CreditRatingSelector } from './PersonalLoanCalculator/components/CreditRatingSelector'
import { LoanDetailsForm } from './PersonalLoanCalculator/components/LoanDetailsForm'
import { PaymentEstimateCard } from './PersonalLoanCalculator/components/PaymentEstimateCard'
import { ClinicFeeDisplay } from './PersonalLoanCalculator/components/ClinicFeeDisplay'
import { createCalculatorCreditApp } from './PersonalLoanCalculator/personalLoanCalculator-utils'

const PersonalLoanCalculator = () => {
  const { t } = useTranslation()
  const user = useUser()

  const { control, setValue } = useForm<PersonalLoanCalculatorSchemaType>({
    mode: 'onBlur',
    resolver: yupResolver(PersonalLoanCalculatorSchema),
    defaultValues: {
      amountRequested: PersonalLoanCalculatorConstants.DEFAULT_AMOUNT,
    },
  })

  const watchedValues = useWatch({ control })
  const { amountRequested, paymentPlanId, term } = watchedValues

  const [creditRatingInterestRate, setCreditRatingInterestRate] = useState<number>(CreditRatingOptions[0].interestRate)
  const [showClinicFee, setShowClinicFee] = useState<boolean>(false)

  const isBanner = user?.isBannerUser ?? false
  const [subsidiaries] = useMerchantByParentId(isBanner ? (user?.merchantId ?? '') : '')
  const [selectedMerchant, setSelectedMerchant] = useState<Merchant | undefined>(undefined)

  const effectiveMerchantId = isBanner ? (selectedMerchant?.id ?? '') : (user?.merchantId ?? '')
  const inputsDisabled = isBanner && !selectedMerchant

  const [merchant, isLoadingMerchant] = useMerchantById(effectiveMerchantId)

  const calculatorCreditApp = createCalculatorCreditApp()

  const paymentOptionsResult = usePaymentOptions({
    merchant,
    calculatorCreditApp,
  })
  const { merchantPaymentPlans, merchantPayments } = paymentOptionsResult

  const { calculatedRate, isFetching, possibleTerms } = useCalculatorData({
    amountRequested,
    paymentPlanId,
    term,
    creditRatingInterestRate,
    merchantId: effectiveMerchantId,
  })

  const termOptionsResult = useTermOptions({
    paymentPlanId,
    merchantPaymentPlans: merchantPaymentPlans,
    possibleTerms,
  })
  const { loanTermChoicesList } = termOptionsResult

  const validatedPaymentData = useMemo(() => {
    return (merchantPayments as PersonalLoanCalculatorPaymentOption[]) || []
  }, [merchantPayments])

  const validatedTermData = useMemo(() => {
    return (loanTermChoicesList as PersonalLoanCalculatorTermOption[]) || []
  }, [loanTermChoicesList])

  const isDataLoading = isLoadingMerchant || !merchantPayments
  useAutoSelection({
    isDataLoading,
    merchantPayments: merchantPayments as PersonalLoanCalculatorPaymentOption[],
    loanTermChoicesList: loanTermChoicesList as PersonalLoanCalculatorTermOption[],
    paymentPlanId,
    term,
    setValue,
  })

  if (!isLoadingMerchant && !merchant && effectiveMerchantId) {
    return <Typography variant="h6">Merchant information not found.</Typography>
  }

  return (
    <>
      <Typography variant="h3" component="h3" gutterBottom>
        {t('calculator.personalCalculatorTitle')}
      </Typography>
      <Typography variant="h6" component="h6" gutterBottom>
        {t('calculator.personalCalculatorSubtitle')}
      </Typography>

      <Grid container spacing={4}>
        <Grid size={PersonalLoanCalculatorGridSizes.MAIN_CONTENT}>
          {isBanner && (
            <Grid sx={{ mb: 3 }}>
              <Autocomplete
                options={subsidiaries ?? []}
                value={selectedMerchant}
                onChange={(_, newValue) => setSelectedMerchant(newValue ?? undefined)}
                getOptionLabel={(option) => option.name}
                getOptionDisabled={(option) => !option.isActive}
                disableClearable
                fullWidth
                renderInput={(params) => <TextField {...params} label={t('calculator.selectMerchant')} />}
              />
            </Grid>
          )}

          <LoanDetailsForm
            control={control}
            merchantPaymentData={validatedPaymentData}
            termData={validatedTermData}
            stableTermData={validatedTermData}
            disabled={inputsDisabled}
          />

          <CreditRatingSelector
            creditRatingInterestRate={creditRatingInterestRate}
            onRatingChange={setCreditRatingInterestRate}
            disabled={inputsDisabled}
          />

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <ClinicFeeDisplay
              showClinicFee={showClinicFee}
              onToggleClinicFee={() => setShowClinicFee(!showClinicFee)}
              calculatedRate={calculatedRate}
              amountRequested={amountRequested}
            />
          </Grid>

          <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
            {t('calculator.paymentEstimate', { creditRatingInterestRate })}
          </Typography>
        </Grid>

        <Grid size={PersonalLoanCalculatorGridSizes.PAYMENT_CARD}>
          <PaymentEstimateCard isFetching={isFetching} calculatedRate={calculatedRate} />
        </Grid>
      </Grid>
    </>
  )
}
export default memo(PersonalLoanCalculator)
